import { weightedPick } from "../../core/util.js";

/** Euclidean distance in grid cells. */
export const gdist = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);

/**
 * Scatter props onto candidate floor cells. `cells` is a list of [gx,gy]. Each
 * cell has `density` chance of getting a prop chosen (weighted) from the theme
 * palette. Cells near an `avoid` point are skipped so we never bury the
 * entrance/exit.
 */
export function scatterProps(level, theme, cells, density, avoid = []) {
  const palette = theme.props.filter((p) => p.weight > 0);
  if (!palette.length) return;
  for (const [gx, gy] of cells) {
    if (Math.random() > density) continue;
    if (avoid.some(([ax, ay]) => gdist(gx, gy, ax, ay) < 2.5)) continue;
    const kind = weightedPick(palette);
    level.addProp({
      gx: gx + 0.5, gy: gy + 0.5,
      shape: kind.shape, color: kind.color, blocking: kind.blocking,
      breakable: kind.breakable, table: kind.table,
      scale: kind.scale || 1, rot: Math.random() * Math.PI * 2,
      key: theme.assets && theme.assets[kind.shape] ? theme.assets[kind.shape] : null,
    });
  }
}

/** Collect all currently-walkable cells as [gx,gy]. */
export function walkableCells(level) {
  const out = [];
  for (let y = 0; y < level.gridH; y++)
    for (let x = 0; x < level.gridW; x++)
      if (level.walkableGrid(x, y)) out.push([x, y]);
  return out;
}

/**
 * Finalize any level once its geometry + entrance are set:
 *  - places the exit at the farthest cell REACHABLE from the entrance
 *    (guarantees the exit is always walkable-to)
 *  - filters spawn candidates to reachable cells a safe distance from start
 * Works for every theme, so generators don't each reinvent connectivity.
 */
export function finalize(level, minSpawnDist = 6) {
  const sx = Math.floor(level.playerStart.gx), sy = Math.floor(level.playerStart.gy);
  const { seen } = level.reachableFrom(sx, sy);

  let best = [sx, sy], bd = -1;
  for (let y = 0; y < level.gridH; y++)
    for (let x = 0; x < level.gridW; x++) {
      if (!seen[y][x]) continue;
      const d = gdist(x, y, sx, sy);
      if (d > bd) { bd = d; best = [x, y]; }
    }
  level.exit = { gx: best[0], gy: best[1] };

  // keep only reachable spawns that aren't on top of the player
  const filtered = level.spawnPoints.filter((s) => {
    const x = Math.floor(s.gx), y = Math.floor(s.gy);
    return seen[y] && seen[y][x] && gdist(x, y, sx, sy) > minSpawnDist;
  });
  if (filtered.length) level.spawnPoints = filtered;
  return level;
}
